/**
 * LE CATALOGUE DES TYPES D'ALARME, POUR LA PAGE DU CATALOGUE (LOT F4).
 * ============================================================================
 *
 * INTENTION : avant de créer une surveillance, le client parcourt ce qu'on
 * peut surveiller chez lui, et lit le compte honnête de ce qui n'est pas
 * proposé.
 *
 * La route 1 (`GET /alarmes-v2/type`) sert les types publiés, chacun par liste
 * blanche. Sur un équipement désigné (`objet_type` et `_id_objet`), elle retire
 * les types qu'aucune de ses sources n'écrit et les compte (`types_ecartes`),
 * et compte à part ceux qu'elle garde sans savoir (`types_non_rattaches`).
 * Sans équipement, les deux compteurs valent `null` : rien n'a été compté, et
 * l'écran ne dit aucun nombre (jamais 0).
 *
 * Un compteur servi qui n'est pas un entier positif ou nul est lu `null` : on
 * ne sait pas, on ne dit rien. Un élément sans `code` ou sans `version`, ou qui
 * porterait un `statut` autre que `publie` (la façade ne le sert pas
 * aujourd'hui : garde de principe), n'est pas montré.
 *
 * Tenu par session (personne et entreprise) et par équipement désigné : une fin
 * de lecture qui n'est plus la courante est jetée sans toucher à l'état, en
 * succès comme en échec.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type {
    AlarmesV2FiltresCatalogue,
    AlarmesV2TypeAlarme,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** Où en est la lecture du catalogue. */
export type StatutCatalogueComplet =
    | "attente"
    | "chargement"
    | "pret"
    | "erreur"

/** Un compteur servi : un entier positif ou nul, sinon `null` (on ne sait pas). */
function compteur(v: unknown): number | null {
    return typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null
}

/** Vrai pour un type montrable : un code, une version, et publié s'il le dit. */
export function estMontrable(type: unknown): type is AlarmesV2TypeAlarme {
    if (type === null || typeof type !== "object") return false
    const t = type as Record<string, unknown>
    if (typeof t.code !== "string" || t.code === "") return false
    if (typeof t.version !== "number" || !Number.isInteger(t.version))
        return false
    // Garde de principe : la liste blanche ne sert pas `statut` (types publiés seuls).
    if ("statut" in t && t.statut !== "publie") return false
    return true
}

/**
 * Garde la version la plus récente de chaque code : le catalogue montre un
 * type une fois.
 */
function derniereVersion(types: AlarmesV2TypeAlarme[]): AlarmesV2TypeAlarme[] {
    const parCode = new Map<string, AlarmesV2TypeAlarme>()
    for (const type of types) {
        const connu = parCode.get(type.code)
        if (!connu || type.version > connu.version) parCode.set(type.code, type)
    }
    return [...parCode.values()]
}

const cleDe = (session: string, f: AlarmesV2FiltresCatalogue) =>
    session ? `${session}|${f.objet_type ?? ""}|${f._id_objet ?? ""}` : ""

export const useCatalogueStore = defineStore("alarmesV2Catalogue", () => {
    /** Les types montrables, une version par code. */
    const types = ref<AlarmesV2TypeAlarme[]>([])

    /** Types écartés sur l'équipement désigné ; `null` : rien n'a été compté. */
    const typesEcartes = ref<number | null>(null)

    /** Types gardés sans savoir si l'équipement les mesure ; `null` : non compté. */
    const typesNonRattaches = ref<number | null>(null)

    const statut = ref<StatutCatalogueComplet>("attente")
    const erreur = ref<ErreurAlarmesV2 | null>(null)

    /** La clé de la lecture faite ou en cours (session et équipement). */
    const cleLue = ref("")

    let lectureCourante = 0

    function vider(): void {
        types.value = []
        typesEcartes.value = null
        typesNonRattaches.value = null
        erreur.value = null
    }

    /**
     * Lit le catalogue pour la session et l'équipement donnés. Ne rejette
     * jamais. Sans `relire`, une lecture faite pour la même clé sert telle
     * quelle ; avec `relire` (chaque visite de la page), le catalogue et ses
     * comptes sont relus, jamais servis périmés.
     * @param filtres - l'équipement désigné, ou rien
     * @param options - `relire` : relire même si la clé a déjà été lue
     */
    async function charger(
        filtres: AlarmesV2FiltresCatalogue = {},
        options: { relire?: boolean } = {}
    ): Promise<void> {
        const cle = cleDe(cleDeSession(), filtres)
        if (!cle) {
            reinitialiser()
            return
        }
        if (
            !options.relire &&
            cleLue.value === cle &&
            statut.value !== "erreur"
        )
            return

        const numero = ++lectureCourante
        cleLue.value = cle
        vider()
        statut.value = "chargement"
        const estCourante = () =>
            numero === lectureCourante && cleDe(cleDeSession(), filtres) === cle
        try {
            const reponse =
                await serviceAlarmesV2.alarmesV2ListerCatalogue(filtres)
            if (!estCourante()) return
            if (!reponse || !Array.isArray(reponse.items)) {
                // Une réponse illisible n'est pas un catalogue vide.
                erreur.value = new ErreurAlarmesV2({
                    code: CODE_INDISPONIBLE,
                    statut: null,
                })
                statut.value = "erreur"
                return
            }
            types.value = derniereVersion(reponse.items.filter(estMontrable))
            typesEcartes.value = compteur(reponse.types_ecartes)
            typesNonRattaches.value = compteur(reponse.types_non_rattaches)
            statut.value = "pret"
        } catch (e) {
            if (!estCourante()) return
            erreur.value =
                e instanceof ErreurAlarmesV2 ? e : versErreurAlarmesV2(e)
            statut.value = "erreur"
        }
    }

    /** Remet le store à son état de départ ; toute lecture en vol est jetée. */
    function reinitialiser(): void {
        lectureCourante += 1
        vider()
        statut.value = "attente"
        cleLue.value = ""
    }

    return {
        types,
        typesEcartes,
        typesNonRattaches,
        statut,
        erreur,
        cleLue,
        charger,
        reinitialiser,
    }
})
